/**
 * React Hooks for Matter Files (V2 - Full React Query)
 *
 * This module provides custom hooks for managing file state and operations
 * using React Query with client-side API calls instead of server actions.
 */

'use client';

import { useCallback, useState } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { FRESH_DATA_CONFIG } from '../query-config';
import { filesApi } from './client';
import { queryKeys } from '@/lib/react-query';
import {
  useDeleteFile as useDeleteFileMutation,
  useUpdateFile as useUpdateFileMutation,
  useUncategoriseFile as useUncategoriseFileMutation,
  useCategoriseFile as useCategoriseFileMutation,
  useAssignIdentityDocument as useAssignIdentityDocumentMutation,
  useUploadFile as useUploadFileMutation // This now uses server action
} from './mutations';
import type {
  FileListResponse,
  UpdateFileRequest,
  FileFilters,
  FileType,
  CoreSlotFileType,
  IdentitySlotFileType
} from './types';
import { validateFile } from './utils';

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Hook to fetch all files for a matter using client-side API
 */
export function useMatterFiles(
  matterId: number,
  filters?: FileFilters,
  options?: Omit<UseQueryOptions<FileListResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.files.list(matterId, filters),
    queryFn: () => filesApi.getFiles(matterId, filters),
    ...FRESH_DATA_CONFIG,
    enabled: !!matterId && !isNaN(matterId),
    ...options
  });
}

// ============================================================================
// MUTATION HOOKS WITH ENHANCED FEATURES
// ============================================================================

/**
 * Hook for file upload with progress tracking
 * Now uses server action for uploads
 */
export function useFileUpload(matterId: number) {
  const _queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const uploadMutation = useUploadFileMutation(); // This now uses server action

  const uploadFile = useCallback(
    async (
      file: File,
      fileType: FileType,
      options?: {
        displayName?: string;
        isCertifiedCopy?: boolean;
        analysisRequested?: boolean;
        contactId?: number;
        institutionId?: number;
        institutionFormId?: number;
        signatoryContactId?: number;
        inventoryItemId?: number;
        attachmentId?: number; // For correspondence attachments
        matterId?: number; // For correspondence attachments
        metadata?: Record<string, unknown>;
        /** Real S3-PUT progress, 0–100 (LF-4771). */
        onProgress?: (percent: number) => void;
        /** Cancel the in-flight upload. */
        signal?: AbortSignal;
      }
    ) => {
      try {
        // Validate file
        const validation = validateFile(file, fileType);
        if (!validation.valid) {
          throw new Error(validation.error);
        }

        // Set initial progress
        setUploadProgress(10);

        // Use contactId from options directly, or from metadata for backward compatibility
        const metadataContactId = options?.metadata?.contactId;
        const contactId =
          options?.contactId ||
          (typeof metadataContactId === 'number'
            ? metadataContactId
            : undefined);

        // Use the mutation (which now uses server action internally)
        const result = await uploadMutation.mutateAsync({
          matterId: options?.matterId || matterId,
          file,
          fileType: fileType as string,
          isCertifiedCopy: options?.isCertifiedCopy,
          analysisRequested: options?.analysisRequested,
          displayName: options?.displayName,
          contactId: contactId,
          institutionId: options?.institutionId,
          institutionFormId: options?.institutionFormId,
          signatoryContactId: options?.signatoryContactId,
          inventoryItemId: options?.inventoryItemId,
          attachmentId: options?.attachmentId,
          metadata: options?.metadata,
          onProgress: options?.onProgress,
          signal: options?.signal
        });

        setUploadProgress(100);

        // The server action handles displayName and isCertifiedCopy

        return result;
      } finally {
        // Reset progress after a delay
        setTimeout(() => setUploadProgress(0), 1000);
      }
    },
    [matterId, uploadMutation]
  );

  return {
    uploadFile,
    uploadProgress,
    isUploading: uploadMutation.isPending,
    error: uploadMutation.error
  };
}

/**
 * Hook to handle file download/preview
 * Uses the correct backend API endpoint through the proxy
 */
export function useFileDownload(_matterId: number) {
  return useMutation({
    mutationFn: async ({
      fileId,
      preview
    }: {
      fileId: number;
      preview?: boolean;
    }) => {
      // Use the files API route which handles auth and redirects to S3
      const baseUrl = `/api/files/${fileId}/download`;
      const url = preview ? `${baseUrl}?preview=true` : baseUrl;

      return url;
    },
    onSuccess: (url) => {
      // Open in new tab for preview or download
      window.open(url, '_blank');
    },
    onError: (error) => {
      toast.error(
        'Failed to access file. The file may have been deleted or is temporarily unavailable.'
      );
      console.error('File access error:', error);
    }
  });
}

// ============================================================================
// COMPOUND SERVICE HOOK
// ============================================================================

/**
 * Hook providing all file operations for a matter using React Query
 * This replaces the server action-based version
 */
export function useMatterFilesService(matterId: number, filters?: FileFilters) {
  // Queries
  const filesQuery = useMatterFiles(matterId, filters);

  // Mutations
  const uploadHook = useFileUpload(matterId);
  const updateMutation = useUpdateFileMutation();
  const uncategoriseMutation = useUncategoriseFileMutation();
  const categoriseMutation = useCategoriseFileMutation();
  const assignIdentityMutation = useAssignIdentityDocumentMutation();
  const deleteMutation = useDeleteFileMutation();
  const downloadMutation = useFileDownload(matterId);

  const updateFile = useCallback(
    async (fileId: number, updates: UpdateFileRequest) => {
      return updateMutation.mutateAsync({
        matterId,
        fileId,
        data: updates
      });
    },
    [matterId, updateMutation]
  );

  const deleteFile = useCallback(
    async (fileId: number) => {
      return deleteMutation.mutateAsync({ matterId, fileId });
    },
    [matterId, deleteMutation]
  );

  const uncategoriseFile = useCallback(
    async (fileId: number) => {
      return uncategoriseMutation.mutateAsync({ matterId, fileId });
    },
    [matterId, uncategoriseMutation]
  );

  const categoriseFile = useCallback(
    async (fileId: number, fileType: CoreSlotFileType) => {
      return categoriseMutation.mutateAsync({ matterId, fileId, fileType });
    },
    [matterId, categoriseMutation]
  );

  const assignIdentityDocument = useCallback(
    async (
      fileId: number,
      contactId: number,
      fileType: IdentitySlotFileType,
      contactName?: string
    ) => {
      return assignIdentityMutation.mutateAsync({
        matterId,
        fileId,
        contactId,
        fileType,
        contactName
      });
    },
    [matterId, assignIdentityMutation]
  );

  const downloadFile = useCallback(
    async (fileId: number, preview?: boolean) => {
      return downloadMutation.mutateAsync({ fileId, preview });
    },
    [downloadMutation]
  );

  return {
    // Data
    files: filesQuery.data?.files || [],
    totalFiles: filesQuery.data?.total || 0,

    // Loading states
    isLoading: filesQuery.isLoading, // true only when no cached data
    isFetching: filesQuery.isFetching, // true during any fetch (including background)
    isError: filesQuery.isError,
    error: filesQuery.error,

    // Operations
    uploadFile: uploadHook.uploadFile,
    updateFile,
    uncategoriseFile,
    categoriseFile,
    assignIdentityDocument,
    deleteFile,
    downloadFile,

    // Mutation states
    isUploading: uploadHook.isUploading,
    uploadProgress: uploadHook.uploadProgress,
    isUpdating: updateMutation.isPending,
    isUncategorising: uncategoriseMutation.isPending,
    isCategorising: categoriseMutation.isPending,
    isAssigningIdentity: assignIdentityMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDownloading: downloadMutation.isPending,

    // Refetch
    refetch: filesQuery.refetch
  };
}
